// src/components/RoomsInventory.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import { BsPencilSquare } from "react-icons/bs";
import { FaSpinner } from "react-icons/fa";

import {
  getAvailableRooms,
  getContents,
  addContent,
  getPhysicalRooms,
} from "../../../FIREBASECONFIG"; // adjust path

import "bootstrap/dist/css/bootstrap.min.css";

const ACTION_COL_W = 60;
const ROOM_TYPE_COL_W = 220;
const DAY_COL_W = 140;

const BORDER = "1px solid #dee2e6";

const cellBase = {
  border: BORDER,
  padding: "8px 10px",
  fontSize: 13,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

const stickyCell = (left, background, zIndex = 2) => ({
  ...cellBase,
  position: "sticky",
  left,
  background,
  zIndex,
});

const RoomsInventory = ({ hotelId }) => {
  const [loading, setLoading] = useState(true);
  const [roomsDataSet, setRoomsDataSet] = useState([]);
  const [days, setDays] = useState([]);

  // Filters
  const [filterStartDate, setFilterStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [filterEndDate, setFilterEndDate] = useState(
    moment().add(29, "days").format("YYYY-MM-DD")
  );

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    roomId: "",
    planId: "",
    planName: "",
    roomName: "",
  });
  const [price, setPrice] = useState("");
  const [modalStartDate, setModalStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [modalEndDate, setModalEndDate] = useState(
    moment().add(29, "days").format("YYYY-MM-DD")
  );
  const [saving, setSaving] = useState(false);

  // Update days array whenever filters change
  useEffect(() => {
    const start = moment(filterStartDate);
    const end = moment(filterEndDate);
    const daysCount = end.diff(start, "days") + 1;
    const arr = [];
    for (let i = 0; i < daysCount; i++) {
      arr.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
    }
    setDays(arr);
  }, [filterStartDate, filterEndDate]);

  const fetchData = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const allReservations = await getContents("reservations");
      const hotelReservations = allReservations.filter(
        (r) =>
          r.hotelId === hotelId &&
          (r.status || "").toLowerCase() !== "cancelled (hide)" &&
          (r.status || "").toLowerCase() !== "no-show"
      );

      const availableRooms = await getAvailableRooms(
        hotelId,
        filterStartDate,
        filterEndDate
      );

      const dataset = [];
      const physicalStatsCache = {};

      for (const room of availableRooms) {
        if (!room.plans || room.plans.length === 0) continue;

        if (!physicalStatsCache[room.id]) {
          const units = await getPhysicalRooms(room.id);
          const total = Number(
            room.totalRoom || units.length || room.plans[0]?.totalPlan || 0
          );
          const blocked = units.filter((u) => u.status === "blocked").length;
          const available = units.length
            ? units.filter((u) => u.status === "available").length
            : Math.max(total - blocked, 0);

          physicalStatsCache[room.id] = { total, available, blocked };
        }

        const roomStats = physicalStatsCache[room.id];

        for (const plan of room.plans) {
          const row = {
            ...plan,
            roomId: room.id,
            roomName: room.name,
            name: plan.name || plan.planName || "",
            roomsAvailable: Number(plan.totalPlan || plan.roomsAvailable || 0),
            roomTotal: roomStats.total,
            roomAvailable: roomStats.available,
            roomBlocked: roomStats.blocked,
          };

          days.forEach((d, idx) => {
            const soldCount = hotelReservations.reduce((acc, res) => {
              const checkIn = moment(res.checkIn, "YYYY-MM-DD");
              const checkOut = moment(res.checkOut, "YYYY-MM-DD");
              const dayMoment = moment(d, "YYYY-MM-DD");

              const inRange =
                dayMoment.isSameOrAfter(checkIn, "day") &&
                dayMoment.isBefore(checkOut, "day");

              if (!inRange) return acc;

              const qty = (res.roomDetails || []).reduce((qacc, det) => {
                if (det.id === room.id && det.planId === plan.id) {
                  return qacc + Number(det.quantity || 0);
                }
                return qacc;
              }, 0);

              return acc + qty;
            }, 0);

            row[`day${idx + 1}`] = soldCount;
          });

          dataset.push(row);
        }
      }

      setRoomsDataSet(dataset);
    } catch (err) {
      console.error("RoomsInventory load error:", err);
    } finally {
      setLoading(false);
    }
  }, [hotelId, days, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group plan rows by room type, with per-day availability for the group row
  const roomGroups = useMemo(() => {
    const groups = new Map();

    roomsDataSet.forEach((row) => {
      if (!groups.has(row.roomId)) {
        groups.set(row.roomId, {
          roomId: row.roomId,
          roomName: row.roomName,
          total: Number(row.roomTotal || 0),
          blocked: Number(row.roomBlocked || 0),
          plans: [],
        });
      }
      groups.get(row.roomId).plans.push(row);
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      dailyStats: days.map((_, idx) => {
        const sold = group.plans.reduce(
          (sum, plan) => sum + Number(plan[`day${idx + 1}`] || 0),
          0
        );
        return {
          sold,
          available: Math.max(group.total - group.blocked - sold, 0),
        };
      }),
    }));
  }, [roomsDataSet, days]);

  const openRateModal = (plan) => {
    setModalData({
      roomId: plan.roomId,
      planId: plan.id,
      planName: plan.name || plan.planName || "",
      roomName: plan.roomName,
    });
    setPrice("");
    setModalStartDate(filterStartDate);
    setModalEndDate(filterEndDate);
    setShowModal(true);
  };

  const saveInventory = async () => {
    if (!modalData.planId || !modalData.roomId) return;
    if (!price) {
      alert("Please provide a price.");
      return;
    }
    if (!modalStartDate || !modalEndDate) {
      alert("Please select a start and end date.");
      return;
    }
    if (
      !moment(modalEndDate, "YYYY-MM-DD").isSameOrAfter(
        moment(modalStartDate, "YYYY-MM-DD"),
        "day"
      )
    ) {
      alert("End date must be on or after the start date.");
      return;
    }
    setSaving(true);

    const payload = {
      hotel_id: hotelId,
      room_id: modalData.roomId,
      rateplan_id: modalData.planId,
      price: Number(price),
      startDate: modalStartDate,
      endDate: modalEndDate,
      priority: new Date().toISOString(),
      active: true,
    };

    try {
      await addContent("inventory", "", payload, null);
      setShowModal(false);
      await fetchData();
    } catch (err) {
      console.error("Failed to add inventory:", err);
      alert("Failed to save inventory.");
    } finally {
      setSaving(false);
    }
  };

  // Previous / Next month buttons
  const handlePrevMonth = () => {
    const newStart = moment(filterStartDate).subtract(1, "month");
    const newEnd = moment(newStart).add(29, "days");
    setFilterStartDate(newStart.format("YYYY-MM-DD"));
    setFilterEndDate(newEnd.format("YYYY-MM-DD"));
  };

  const handleNextMonth = () => {
    const newStart = moment(filterStartDate).add(1, "month");
    const newEnd = moment(newStart).add(29, "days");
    setFilterStartDate(newStart.format("YYYY-MM-DD"));
    setFilterEndDate(newEnd.format("YYYY-MM-DD"));
  };

  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.85)",
            zIndex: 50,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <FaSpinner
              className="spin"
              style={{ fontSize: 28, marginBottom: 8 }}
            />
            <div>Loading inventory...</div>
          </div>
        </div>
      )}

      {/* Filters Row */}
      <div className="d-flex mb-3 gap-2 align-items-end justify-content-between">
        <div className="d-flex gap-2">
          <div>
            <label className="form-label mb-0">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label mb-0">End Date</label>
            <input
              type="date"
              className="form-control"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={handlePrevMonth}>
            &lt; Previous Month
          </button>
          <button className="btn btn-outline-primary" onClick={handleNextMonth}>
            Next Month &gt;
          </button>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          maxHeight: "65vh",
          overflow: "auto",
          border: BORDER,
          borderRadius: 8,
          background: "#fff",
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  ...stickyCell(0, "#f8f9fa", 5),
                  position: "sticky",
                  top: 0,
                  width: ACTION_COL_W,
                  minWidth: ACTION_COL_W,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                Edit
              </th>
              <th
                style={{
                  ...stickyCell(ACTION_COL_W, "#f8f9fa", 5),
                  position: "sticky",
                  top: 0,
                  width: ROOM_TYPE_COL_W,
                  minWidth: ROOM_TYPE_COL_W,
                  textAlign: "left",
                  fontWeight: 600,
                  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                }}
              >
                Room Type
              </th>
              {days.map((day) => (
                <th
                  key={day}
                  style={{
                    ...cellBase,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    background: "#f8f9fa",
                    width: DAY_COL_W,
                    minWidth: DAY_COL_W,
                    textAlign: "center",
                    fontWeight: 600,
                  }}
                >
                  {moment(day).format("DD MMM YYYY")}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {roomGroups.length === 0 ? (
              <tr>
                <td
                  colSpan={days.length + 2}
                  style={{ ...cellBase, textAlign: "center", color: "#6c757d" }}
                >
                  No room plans found
                </td>
              </tr>
            ) : (
              roomGroups.map((group) => (
                <React.Fragment key={group.roomId}>
                  {/* Room type summary row */}
                  <tr>
                    <td style={stickyCell(0, "#eef1f5")} />
                    <td
                      style={{
                        ...stickyCell(ACTION_COL_W, "#eef1f5"),
                        fontWeight: 600,
                        whiteSpace: "normal",
                        boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                      }}
                    >
                      {group.roomName}
                    </td>
                    {group.dailyStats.map((stat, idx) => (
                      <td
                        key={days[idx]}
                        style={{
                          ...cellBase,
                          background: "#eef1f5",
                          lineHeight: 1.5,
                        }}
                      >
                        Total: {group.total}
                        <br />
                        Available: {stat.available}
                        <br />
                        Blocked: {group.blocked}
                      </td>
                    ))}
                  </tr>

                  {/* Rate plan rows */}
                  {group.plans.map((plan) => (
                    <tr key={plan.id}>
                      <td
                        style={{
                          ...stickyCell(0, "#ffffff"),
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => openRateModal(plan)}
                        title="Update rate"
                      >
                        <BsPencilSquare
                          size={17}
                          style={{ color: "var(--secondary)" }}
                        />
                      </td>
                      <td
                        style={{
                          ...stickyCell(ACTION_COL_W, "#ffffff"),
                          whiteSpace: "normal",
                          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                        }}
                      >
                        {plan.roomName}
                        <div style={{ fontSize: 12, color: "#6c757d" }}>
                          {plan.name || ""}
                        </div>
                      </td>
                      {days.map((day, idx) => {
                        const priceObj = (plan.nightlyPrices || []).find(
                          (p) => p.date === day
                        );
                        return (
                          <td
                            key={day}
                            style={{ ...cellBase, lineHeight: 1.5 }}
                          >
                            Sold: {Number(plan[`day${idx + 1}`] || 0)}
                            <br />
                            Rate:{" "}
                            {Number(priceObj?.price || 0).toLocaleString()}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-md modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Inventory</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowModal(false)}
                  />
                </div>
                <div className="modal-body">
                  {/* <div className="mb-2">
                    <strong>Hotel:</strong> {hotelId}
                  </div> */}
                  <div className="mb-2">
                    <strong>Room:</strong> {modalData.roomName}
                  </div>
                  <div className="mb-2">
                    <strong>Plan:</strong> {modalData.planName}
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={modalStartDate}
                        onChange={(e) => setModalStartDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={modalEndDate}
                        min={modalStartDate}
                        onChange={(e) => setModalEndDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Enter price per night"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveInventory}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Inventory"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
        </>
      )}
    </div>
  );
};

export default RoomsInventory;
